import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import {
  clearSelectedCity,
  selectPickingOrigin,
  startMovementToCity,
  stopPickingOrigin,
} from "src/store/ui"
import { selectGamePlayers, selectMetadataCells, selectViewerId } from "src/store/game"
import { selectGameConfig } from "src/store/common"
import { cellDisplayName } from "src/utils/maps"
import { settlementCostFor } from "src/utils/cellActions"
import { startingCitiesOf } from "src/utils/pendingOrders"
import { actionForKey, isPlainKeyPress } from "src/utils/shortcuts"
import useShortcuts from "src/hooks/useShortcuts"
import ModalService from "src/modals"
import { CellOrderButtons, useCellOrders } from "src/components/CellOrders"

import "./MapActionBar.css"

/* How much of the window, from the bar's top edge down, a sheet over the map must leave free
   (owner, 2026-09-30: "the modal should always leave space to the action modal"). Published on
   the document element while the bar is up, measured off the bar itself, so the city sheet
   (`.modal-above-action-bar`, Modal.css) ends above it on every shell whatever the bar's height —
   one row or two, an origin list, a wrapped reason, the phone's paused-notice lift. */
export const ACTION_BAR_CLEAR_VAR = "--map-action-bar-clear"

/*
The bar the map answers a click with — the only bar: hovering shows a tooltip, selecting shows
this. Every order the cell admits is a button; an order the server would refuse is still there but
disabled, and the bar says why (no actions left, no troops here, silo reloading…) so "can I press it?" and "will it work?" are the same question. The rules live in
`utils/cellActions.js`, mirrored from the server's use cases.
*/
const MapActionBar = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const orders = useCellOrders()
  const city = orders?.city ?? null
  const cells = useSelector(selectMetadataCells)
  const userId = useSelector(selectViewerId)
  const players = useSelector(selectGamePlayers)
  const gameConfig = useSelector(selectGameConfig)
  const [hint, setHint] = useState(null)
  // "March here" needs an origin: while picking one, the buttons give way to the garrison list.
  // Held in the store so that Escape and a new selection end it (see `pickingOrigin` there).
  const pickingOrigin = useSelector(selectPickingOrigin)
  const shortcuts = useShortcuts()

  // Keyboard: the bound key fires the same handler the button does, under the same conditions —
  // a cell selected, the order enabled, nothing typed into a field, no modal on top. The listener
  // reads the latest actions/handlers through a ref so it judges the same state the buttons show.
  const latest = useRef({ actions: [], handlers: {} })
  useEffect(() => {
    if (!city) return undefined
    const onKeyDown = (e) => {
      // The city view is a lens on the selected cell, not a different place — the cell orders
      // stay reachable while it is open (owner's P12), the battle key included. Any other modal
      // still swallows the keys.
      const onlyCityView = (ModalService.stackTypes ?? []).every((type) => type === "CityModal")
      if (!isPlainKeyPress(e) || (ModalService.hasOpenModals() && !onlyCityView)) return
      const action = actionForKey(shortcuts, e.key)
      if (!action) return
      e.preventDefault()
      // One key for both marches: from here when you stand here, to here when you do not.
      // Share is not a cell order the rules engine knows — it is always available on a selected
      // cell, so it bypasses the enabled-actions lookup.
      if (action === "share") {
        latest.current.handlers.share?.()
        return
      }
      const target =
        latest.current.actions.find((a) => a.key === action) ||
        (action === "march" ? latest.current.actions.find((a) => a.key === "marchTo") : undefined)
      if (target?.enabled) latest.current.handlers[target.key]()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [city, shortcuts])

  // The room a sheet leaves for this bar: measured every render (the bar changes shape with the
  // cell) and whenever it resizes or the window does; withdrawn when the bar goes.
  const barRef = useRef(null)
  const publishClearance = useRef(() => {})
  publishClearance.current = () => {
    const bar = barRef.current
    if (!bar) return
    const clear = Math.max(0, Math.round(window.innerHeight - bar.getBoundingClientRect().top))
    document.documentElement.style.setProperty(ACTION_BAR_CLEAR_VAR, `${clear}px`)
  }
  const hasBar = Boolean(city)
  useLayoutEffect(() => {
    if (!hasBar) return undefined
    const publish = () => publishClearance.current()
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(publish) : null
    if (barRef.current) observer?.observe(barRef.current)
    window.addEventListener("resize", publish)
    return () => {
      observer?.disconnect()
      window.removeEventListener("resize", publish)
      document.documentElement.style.removeProperty(ACTION_BAR_CLEAR_VAR)
    }
  }, [hasBar])
  useLayoutEffect(() => publishClearance.current())

  if (!city) return null

  const { actions, actionsLeft, ownUnits, ownStandingUnits, ownerId, isOwn, origins, handlers, name, isLand, users } =
    orders
  const userMetadata = orders.userMetadata

  const owner = players.find((player) => player.user.id === ownerId)
  // Who else stands on unclaimed ground (owner's R10): an ally's camp used to read as plain
  // "unclaimed", as if their troops were not there.
  const myTeam = userMetadata?.team
  const alliedCampers = Object.entries(cells?.[`${city.col}-${city.row}`]?.metadata_by_user || {})
    .filter(
      ([presentId, metadata]) =>
        Number(presentId) !== userId &&
        users[String(presentId)]?.team === myTeam &&
        Object.values(metadata?.units || {}).some((unit) => (unit?.number ?? 0) > 0)
    )
    .map(([presentId]) => players.find((player) => player.user.id === Number(presentId))?.user.username)
    .filter(Boolean)
  const troopsHere = ownUnits + (ownStandingUnits || 0)
  /* Unowned ground says what claiming it costs, in the team's own coin, where it used to say
     "unclaimed" (owner, 2026-09-07). The price is the thing a player standing on free land needs
     to know, it climbs with every colony held, and until now it was only quoted on the Settle
     button — which is absent whenever the troops to pay it are not there yet. The same 👷 / 🥩 the
     button carries (Q3), and the same mirrored formula. "Unclaimed" is still the fallback while
     the config has not arrived, since a price of zero would be a lie.
     "Your troops hold it" is gone with it: the troop count is appended on the same line. */
  // The Settle entry, where there is one, already prices this round's pending colonies (owner,
  // 2026-10-02: a colony is founded when the round turns) and knows whether one is ordered here.
  const settleAction = actions.find((action) => action.key === "settle")
  const settleCost =
    settleAction?.cost ??
    settlementCostFor({
      rules: gameConfig?.rules_config,
      cells,
      userId,
      team: myTeam,
      initialLocations: startingCitiesOf(userMetadata),
    })
  // A colony already ordered here says nothing on this line (owner, 2026-10-03: "We don't need a
  // text there for this case"): the claim is drawn on the map and CANCEL COLONY is beside it.
  const unclaimed = !isLand
    ? t("game.mapAction.openSea")
    : settleAction?.ordered
      ? ""
      : settleCost
      ? t(myTeam === "human" ? "game.mapAction.unclaimedPriceWorkers" : "game.mapAction.unclaimedPriceMeat", {
          cost: settleCost,
        })
      : t("game.mapAction.unclaimed")
  const held = owner
    ? isOwn
      ? t("game.mapAction.yours")
      : t("game.mapAction.heldBy", { username: owner.user.username })
    : alliedCampers.length > 0
      ? [unclaimed, t("game.mapAction.alliedCamp", { username: alliedCampers.join(", ") })].filter(Boolean).join(" · ")
      : unclaimed

  latest.current = { actions, handlers }

  const pickOrigin = (origin) => {
    if (ModalService.isShowing("CityModal")) ModalService.hide("CityModal")
    dispatch(
      startMovementToCity({
        origin: { col: origin.col, row: origin.row },
        target: city,
      }),
    )
  }
  const originName = (origin) => cellDisplayName({ row: origin.row, column: origin.col, held: true })

  // The left panel's second line: the hovered button's refusal if there is one, else the budget.
  const status =
    hint ||
    (actionsLeft === 0 ? t("game.mapAction.reason.noActions") : t("game.mapAction.actionsLeft", { count: actionsLeft }))

  return (
    <div ref={barRef} className="map-action-bar" role="toolbar" aria-label={t("game.mapAction.toolbar")}>
      <div className="map-action-target">
        <p className="map-action-name">
          {name}{" "}
          <span className="map-action-coords">
            {city.col}-{city.row}
          </span>
        </p>
        <p className="map-action-held">
          {held}
          {troopsHere > 0 ? `${held ? " · " : ""}${t("game.mapAction.troops", { count: troopsHere })}` : ""}
          {ownStandingUnits > 0 ? ` (${t("game.mapAction.inCombatHere", { count: ownStandingUnits })})` : ""}
        </p>
        <p className={`map-action-status${hint || actionsLeft === 0 ? " blocked" : ""}`}>{status}</p>
      </div>

      {pickingOrigin ? (
        <div className="map-action-origins" role="listbox" aria-label={t("game.mapAction.originPicker.sendFrom")}>
          <p className="map-action-origins-title">{t("game.mapAction.originPicker.sendFrom")}</p>
          <div className="map-action-origins-list with-scroll-bar vertical">
            {origins.map((origin) => (
              <button
                key={`${origin.col}-${origin.row}`}
                type="button"
                className="map-action-origin"
                data-action="origin"
                role="option"
                onClick={() => pickOrigin(origin)}
              >
                <span className="map-action-origin-name">
                  {originName(origin)}{" "}
                  <span className="map-action-coords">
                    {origin.col}-{origin.row}
                  </span>
                </span>
                <span className="map-action-origin-troops">
                  {t("game.mapAction.originPicker.troops", {
                    count: origin.units,
                  })}
                </span>
              </button>
            ))}
          </div>
          <button type="button" className="map-action map-action-close" onClick={() => dispatch(stopPickingOrigin())}>
            {t("game.mapAction.originPicker.back")}
          </button>
        </div>
      ) : (
        <div className="map-action-buttons">
          <CellOrderButtons actions={actions} handlers={handlers} onHint={setHint} />
          <button
            type="button"
            className="map-action"
            onClick={handlers.share}
            title={t("game.mapAction.shareTitle")}
          >
            <span className="map-action-icon">📍</span>
            {t("game.mapAction.share")}
            {shortcuts.share && <kbd className="map-action-key">{shortcuts.share.toUpperCase()}</kbd>}
          </button>
          <button
            type="button"
            className="map-action map-action-close"
            onClick={() => dispatch(clearSelectedCity())}
            aria-label={t("game.mapAction.clearSelection")}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  )
}

export default MapActionBar
